import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const location = useLocation();
  
  // Auto-generate breadcrumbs if not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', href: '/' }
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Convert URL segments to readable labels
      let label = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Special cases for better labeling
      if (segment === 'services') {
        label = 'Services';
      } else if (segment === 'pressure-washing') {
        label = 'Pressure Washing';
      } else if (segment === 'window-washing') {
        label = 'Window Cleaning';
      } else if (segment === 'carpet-cleaning') {
        label = 'Carpet Cleaning';
      } else if (segment === 'gutter-cleaning') {
        label = 'Gutter Cleaning';
      } else if (segment === 'solar-panel-cleaning') {
        label = 'Solar Panel Cleaning';
      } else if (segment === 'soft-wash') {
        label = 'Soft Wash';
      }
      
      breadcrumbs.push({
        label,
        href: currentPath
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items || generateBreadcrumbs();

  // Generate JSON-LD schema for breadcrumbs
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": `https://www.venturapowerwashing.com${item.href}`
    }))
  };

  if (breadcrumbItems.length <= 1) {
    return null; // Don't show breadcrumbs on home page
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <nav aria-label="Breadcrumb" className="bg-gray-50 px-4 py-3 border-b">
        <div className="container mx-auto">
          <ol className="flex items-center space-x-2 text-sm">
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              
              return (
                <li key={item.href} className="flex items-center">
                  {index === 0 && <Home className="h-4 w-4 mr-1" />}
                  
                  {isLast ? (
                    <span className="text-gray-600 font-medium" aria-current="page">
                      {item.label}
                    </span>
                  ) : (
                    <>
                      <Link 
                        to={item.href}
                        className="text-ventura-blue hover:text-ventura-darkBlue hover:underline font-medium"
                      >
                        {item.label}
                      </Link>
                      <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                    </>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumbs;